<template>
  <popup
     ref="popup"
     v-if="this.isManager('project|maintenance|finance|admin')"
    :button-style="addProvider ? 'primary' : 'info'"
    :heading="'New Backhaul ' + (sharerName ? 'for ' + sharerName : (addProvider ? 'Provider' + (siteName ? ': ' + siteName : '') : 'Customer'))"
    :show-text="showText"
    :button-text="buttonText || (addProvider ? 'Provider' : 'Customer')"
    size="medium"
    buttonSize="small"
    button-icon="fa fa-plus"
    :disable-button="readOnly"
    :table-ref="tableRef"
    :show-cancel="false"
    :show-save="false"
    @open="onOpenDialog"
    @save="onCloseDialog"
    @close="onCloseDialog">

    <div class="padded" style="clear: both; text-align: left;">

      <backhaul-provider
      v-if="provider"
        id="bkp"
       :key="formKey"
       :site-id="siteId"
       :sharer-id="sharerId"
       :table-ref="tableRef"
       :action-id="actionId"
       :default-provider-id="defaultProviderId"
       :default-backhaul-type="defaultBackhaulType"
       :is-new="true"
       :edit-mode="true"
       :show-edit-button="false"
       @saved="onProviderSaved" />

       <!-- XOL-552 round 3: the "New Backhaul Customer" popup gets the same
            "+ Backhaul" as RTB step 3, above the line table. It is THIS
            component again, in provider mode — Vue 2 resolves the
            self-reference through `name: 'BackhaulAction'`, and the recursion
            stops because the inner instance has provider=true (it takes the
            branch above, never this one). No `sharer-id` (would flip it to
            "Customer"), no `action-id` (there is no RTB), no default type.
            `:table-ref="null"` stops the stray backhaultable-refresh-table
            broadcast. A save closes only the inner popup; the line list
            behind it refreshes and the new line is not selected. -->
       <template v-else>
         <div class="text-right" v-if="isTowerCo()">
           <backhaul-action
             :provider="true"
             button-text="Backhaul"
             :site-id="siteId"
             :site-name="siteName"
             :default-provider-id="sharerBackhaulTwinId"
             :read-only="readOnly"
             :table-ref="null"
             close-on-save
             @saved="onBackhaulLineAdded" />
         </div>

         <backhaul-customer
           ref="bhCustomer"
           id="bks"
          :key="customerKey"
          :site-id="siteId"
          :sharer-id="sharerId"
          :is-new="true"
          :edit-mode="true"
          :show-edit-buton="false" />
       </template>

    </div>

  </popup>

</template>



<script>


import BackhaulProvider from '@/components/backhaul/BackhaulProvider'
import BackhaulCustomer from '@/components/backhaul/BackhaulCustomer'
import { findBackhaulTwinIdForOrg } from '@/lib/backhaulTwin'


export default {

  name: 'BackhaulAction',

  components: {
    BackhaulProvider,
    BackhaulCustomer
  },

  props: {

    showText: {
      type: Boolean,
      default: true
    },

    provider: {
      type: Boolean,
      required: true,
      default: true
    },

    siteId: {
      type: [String, Number],
      default: null
    },

    sharerId: {
      type: [String, Number],
      default: null
    },

    siteName: {
      type: String,
      default: null
    },

    sharerName: {
      type: String,
      default: null
    },

    siteData: {
      type: Object,
      default: null
    },

    tableRef: {
      type: String,
      default: 'backhaultable'
    },

    readOnly: {
      type: Boolean,
      default: false
    },

    // XOL-552: "+ Backhaul" at RTB step 3 reuses this popup. `buttonText`
    // overrides the "Provider" / "Customer" label; `actionId` stamps the new
    // line with the RTB; the two `default*` props pre-fill the form (the
    // provider stays editable); `closeOnSave` closes the popup after a
    // successful save so the refreshed list behind it is visible. Site ->
    // Backhaul passes none of these and behaves exactly as before.
    buttonText: {
      type: String,
      default: null
    },

    actionId: {
      type: Number,
      default: null
    },

    defaultProviderId: {
      type: Number,
      default: null
    },

    defaultBackhaulType: {
      type: String,
      default: null
    },

    closeOnSave: {
      type: Boolean,
      default: false
    }

  },

  computed: {

    // XOL-552 round 3: the Backhaul-category org that shares the sharer's name
    // — the nested "+ Backhaul" Provider default in the customer popup. null in
    // provider mode, without a sharer, or when there is not exactly one twin.
    // NOTE: getConfig() returns a module-level, non-reactive object, so this
    // computed caches its first value; acceptable, as in step3's
    // telcoBackhaulTwinId (the config loads at boot, before any popup opens).
    sharerBackhaulTwinId () {
      if (this.provider || !this.sharerId) return null

      return findBackhaulTwinIdForOrg(this.getConfig().orgGroups, this.sharerId)
    }

  },

  methods: {

    // XOL-552 round 3: the nested "+ Backhaul" saved a line — re-read the
    // customer's line list. Deliberately NOT closing this popup, emitting
    // `saved`, or broadcasting a refresh: the customer form (edits, any picked
    // line) stays as it is, and the new line is not selected.
    onBackhaulLineAdded () {
      if (this.$refs.bhCustomer) {
        this.$refs.bhCustomer.refreshLines()
      }
    },

    onProviderSaved (row) {
      this.$emit('saved', row)

      if (this.closeOnSave) {
        // el-drawer keeps its body after the first open, and BackhaulProvider
        // never resets its form — bump the key so the next open is a fresh form
        // (otherwise it would show the line just saved).
        this.formKey++
        this.$refs.popup.closeModal()
      }
    },

    resetData () {
      this.telco = this.getTableStruct('backhaul' + (this.provider ? '' : '_subscribers'))
      this.telco.site_id = this.siteId ? this.siteId : this.rowData.site_id
    },

    onSaveDialog () {
      if (this.provider) {
        this.$refs.bkp.onAdd(this.success)
      } else {
        this.$refs.bks.onAdd(this.success)
      }

      this.onCloseDialog()
    },

    // el-drawer keeps its body after the first open, and BackhaulCustomer
    // remembers the id of the record it created (XOL-552 onNewSaved) so a
    // second Save in the same open updates it. Re-key on EVERY open so each
    // "+ Customer" starts a new record instead of PUTting over the last one.
    // It has to be `open`, not `close`: Esc / the X / a mask click close the
    // drawer through Popup's :visible.sync without emitting `close`. (The
    // provider branch never renders <backhaul-customer>; the bump is inert.)
    onOpenDialog () {
      this.customerKey++
    },

    onCloseDialog () {
      this.resetData()

      this.$emit('update-stats')

      if (this.tableRef) {
        this.$events.emit(this.tableRef + '-refresh-table')
      }

      // No closeModal() here: it is what emits `close`, i.e. what calls this
      // method. Closing on save goes through onProviderSaved.
    },

    success () {
      if (this.tableRef) {
        this.$events.emit(this.tableRef + '-refresh-table')
      }
    }
  },

  data () {
    return {
      tenor: 10,
      formKey: 0,
      customerKey: 0,
      addProvider: (this.sharerId || !this.provider ? false : true),
      backhaul: this.getTableStruct('backhaul' + (this.provider ? '' : '_subscribers'))
    }
  },

  mounted () {
//    console.log('Add Backhaul: site_id', this.siteId, 'sharerId', this.sharerId, 'siteData', this.siteData)
  }
}

</script>
